import React from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    VStack,
    HStack,
    Text,
    Box,
    Badge,
    SimpleGrid,
    Divider,
} from '@chakra-ui/react';

const RESPONSE_TEMPLATES = {
    likert5: {
        name: '5-Point Likert Scale (Agreement)',
        description: 'Standard agreement scale',
        category: 'Likert Scales',
        choices: [
            'Strongly Disagree',
            'Disagree',
            'Neither Agree nor Disagree',
            'Agree',
            'Strongly Agree',
        ],
    },
    likert5Satisfaction: {
        name: '5-Point Satisfaction Scale',
        description: 'Measure satisfaction levels',
        category: 'Likert Scales',
        choices: [
            'Very Dissatisfied',
            'Dissatisfied',
            'Neutral',
            'Satisfied',
            'Very Satisfied',
        ],
    },
    likert7: {
        name: '7-Point Likert Scale (Agreement)',
        description: 'Extended agreement scale',
        category: 'Likert Scales',
        choices: [
            'Strongly Disagree',
            'Disagree',
            'Somewhat Disagree',
            'Neither Agree nor Disagree',
            'Somewhat Agree',
            'Agree',
            'Strongly Agree',
        ],
    },
    yesNo: {
        name: 'Yes/No',
        description: 'Simple binary choice',
        category: 'Binary',
        choices: ['Yes', 'No'],
    },
    yesNoMaybe: {
        name: 'Yes/No/Maybe',
        description: 'Binary with neutral option',
        category: 'Binary',
        choices: ['Yes', 'No', 'Maybe'],
    },
    truefalse: {
        name: 'True/False',
        description: 'Fact-based binary',
        category: 'Binary',
        choices: ['True', 'False'],
    },
    frequency5: {
        name: '5-Point Frequency Scale',
        description: 'How often something occurs',
        category: 'Frequency',
        choices: [
            'Never',
            'Rarely',
            'Sometimes',
            'Often',
            'Always',
        ],
    },
    frequency7: {
        name: '7-Point Frequency Scale',
        description: 'Extended frequency measurement',
        category: 'Frequency',
        choices: [
            'Never',
            'Very Rarely',
            'Rarely',
            'Occasionally',
            'Frequently',
            'Very Frequently',
            'Always',
        ],
    },
    quality: {
        name: 'Quality Rating',
        description: 'Assess quality',
        category: 'Rating',
        choices: [
            'Poor',
            'Fair',
            'Good',
            'Very Good',
            'Excellent',
        ],
    },
    importance: {
        name: 'Importance Scale',
        description: 'Measure importance',
        category: 'Rating',
        choices: [
            'Not Important',
            'Slightly Important',
            'Moderately Important',
            'Very Important',
            'Extremely Important',
        ],
    },
    approval: {
        name: 'Approval Scale',
        description: 'Approve/disapprove with DK option',
        category: 'Political',
        choices: [
            'Strongly Disapprove',
            'Somewhat Disapprove',
            'Neither Approve nor Disapprove',
            'Somewhat Approve',
            'Strongly Approve',
            "Don't Know / Haven't Heard Enough",
        ],
    },
    favorability: {
        name: 'Favorability Scale',
        description: 'Favorable/unfavorable with DK option',
        category: 'Political',
        choices: [
            'Very Unfavorable',
            'Somewhat Unfavorable',
            'Neither Favorable nor Unfavorable',
            'Somewhat Favorable',
            'Very Favorable',
            "Don't Know / Haven't Heard Enough",
        ],
    },
    likelihood: {
        name: 'Likelihood Scale',
        description: 'Probability of action',
        category: 'Probability',
        choices: [
            'Very Unlikely',
            'Unlikely',
            'Neutral',
            'Likely',
            'Very Likely',
        ],
    },
    nps: {
        name: 'Net Promoter Score (0-10)',
        description: 'Standard NPS scale',
        category: 'Rating',
        choices: [
            '0 - Not at all likely',
            '1',
            '2',
            '3',
            '4',
            '5',
            '6',
            '7',
            '8',
            '9',
            '10 - Extremely likely',
        ],
    },
    multipleChoice: {
        name: 'Multiple Choice (A/B/C/D)',
        description: 'Standard test format',
        category: 'Multiple Choice',
        choices: ['A', 'B', 'C', 'D'],
    },
};

function ResponseTemplates({ isOpen, onClose, onSelectTemplate }) {
    const categories = [...new Set(Object.values(RESPONSE_TEMPLATES).map(t => t.category))];

    const handleSelectTemplate = (template) => {
        onSelectTemplate(template.choices);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Response Templates</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <Text fontSize="sm" color="gray.600" mb={6}>
                        Choose a pre-built template to quickly add standardized response options to your question.
                    </Text>

                    {categories.map(category => {
                        const templatesInCategory = Object.entries(RESPONSE_TEMPLATES)
                            .filter(([_, template]) => template.category === category);

                        return (
                            <Box key={category} mb={6}>
                                <Text fontWeight="bold" fontSize="lg" mb={3}>
                                    {category}
                                </Text>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    {templatesInCategory.map(([key, template]) => (
                                        <Box
                                            key={key}
                                            p={4}
                                            borderWidth="1px"
                                            borderRadius="md"
                                            _hover={{ borderColor: 'blue.500', bg: 'blue.50' }}
                                            cursor="pointer"
                                            onClick={() => handleSelectTemplate(template)}
                                        >
                                            <HStack justify="space-between" mb={2}>
                                                <Text fontWeight="semibold">{template.name}</Text>
                                                <Badge colorScheme="blue">
                                                    {template.choices.length} choices
                                                </Badge>
                                            </HStack>
                                            <Text fontSize="sm" color="gray.600" mb={3}>
                                                {template.description}
                                            </Text>
                                            <Divider mb={2} />
                                            <VStack align="start" spacing={1}>
                                                {template.choices.slice(0, 3).map((choice, index) => (
                                                    <Text key={index} fontSize="xs" color="gray.500">
                                                        • {choice}
                                                    </Text>
                                                ))}
                                                {template.choices.length > 3 && (
                                                    <Text fontSize="xs" color="gray.400">
                                                        + {template.choices.length - 3} more...
                                                    </Text>
                                                )}
                                            </VStack>
                                        </Box>
                                    ))}
                                </SimpleGrid>
                            </Box>
                        );
                    })}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}

export default ResponseTemplates;
export { RESPONSE_TEMPLATES };
